import React, { useState } from 'react';
import { faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { ROLE_DISPLAY_NAMES, UserRole, USER_ROLES } from '../services/invitationService';
import Button from './Button';
import Modal from './Modal';
import Alert from './Alert';

interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
}

export interface ChangeUserRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  currentUserId: string | undefined;
  onRoleChangeSuccess: (userId: string, newRole: UserRole) => void;
}

const ChangeUserRoleModal: React.FC<ChangeUserRoleModalProps> = ({
  isOpen,
  onClose,
  user,
  currentUserId,
  onRoleChangeSuccess
}) => {
  const [selectedRole, setSelectedRole] = useState<UserRole>(
    (user?.role as UserRole) || USER_ROLES.MEMBER
  );
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleRoleChange = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!user) return;

    setError('');
    setLoading(true);

    try {
      onRoleChangeSuccess(user.id, selectedRole);
      onClose();
    } catch (error) {
      setError('Ocorreu um erro ao alterar a função do usuário');
      console.error('Role change error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedRole(e.target.value as UserRole);
  };

  const handleClose = (): void => {
    setError('');
    setSelectedRole((user?.role as UserRole) || USER_ROLES.MEMBER);
    onClose();
  };

  if (!user) return null;

  const isCurrentUser = user.id === currentUserId;
  const modalDescription = `Alterar a função de ${user.name || user.email} na organização`;

  const footerContent = (
    <div className="flex justify-end space-x-3">
      <Button type="button" onClick={handleClose} variant="secondary" disabled={loading}>
        Cancelar
      </Button>
      <Button
        type="submit"
        form="change-role-form"
        leftIcon={faUserEdit}
        variant="primary"
        disabled={loading || selectedRole === user.role}
      >
        {loading ? 'Alterando...' : 'Alterar Função'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Alterar Função do Usuário"
      description={modalDescription}
      footer={footerContent}
    >
      <form id="change-role-form" onSubmit={handleRoleChange} className="space-y-4">
        {error && <Alert variant="error" message={error} />}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
          <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-600">
            <div className="flex items-center space-x-2">
              <span className="font-medium">{user.name || 'Nome não definido'}</span>
              <span className="text-gray-500">({user.email})</span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nova função na organização
          </label>
          <select
            value={selectedRole}
            onChange={handleRoleSelect}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            {Object.entries(ROLE_DISPLAY_NAMES)
              .filter(([roleKey]) => {
                // Hide SAAS_ADMIN from options
                if (roleKey === USER_ROLES.SAAS_ADMIN) return false;

                // Prevent current user from downgrading themselves to member
                if (isCurrentUser && roleKey === USER_ROLES.MEMBER) {
                  return false;
                }

                return true;
              })
              .map(([roleKey, roleLabel]) => (
                <option key={roleKey} value={roleKey}>
                  {roleLabel}
                </option>
              ))}
          </select>
          {isCurrentUser && (
            <p className="mt-1 text-xs text-amber-600">
              Você não pode alterar sua própria função para Membro.
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Coordenadores têm controle total da organização, Membros têm acesso básico.
          </p>
        </div>

        <div className="bg-blue-50 p-3 rounded-md">
          <p className="text-sm text-blue-700">
            <strong>Função atual:</strong> {ROLE_DISPLAY_NAMES[user.role as UserRole]}
            {selectedRole !== user.role && (
              <>
                <br />
                <strong>Nova função:</strong> {ROLE_DISPLAY_NAMES[selectedRole]}
              </>
            )}
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default ChangeUserRoleModal;
